import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, apiDelete } from '../services/api';

export interface AgreementSummary {
  AvtaleID: string;
  Tittel: string;
  Betaler: string;
  Kategori: string;
  Startdato: string;
  Sluttdato: string | null;
  Beloep_gjeldende: number;
  Risiko_sannsynlighet: number;
  Er_aktiv: boolean;
  Sist_endret: string;
  Opprettet_dato: string;
  Avtale_Revisjonsdato: string | null;
  Inntektsmekanikk: string;
  Prislogikk_type: string | null;
}

export interface AgreementDetail extends AgreementSummary {
  Beskrivelse: string | null;
  Beloep_gjelder_fra: string;
  Beloep_revisjonsdato: string | null;
  Faktureringsfrekvens: string;
  Periodiseringsregel: string | null;
  Indeks_type: string | null;
  Variabler: string | null;
  Risiko_kommentar: string | null;
  Oppsigelse_vilkaar: string | null;
  Opprettet_av: string | null;
  Fordeling_json: string | null;
  links: AgreementLink[];
  documents: AgreementDocument[];
  auditHistory: AuditEntry[];
}

export interface AgreementLink {
  BridgeID: string;
  InntektslinjeID: string;
  Beloep_andel: number;
  Andel_prosent: number;
  Enhet: string;
  Ansvar: string;
  Konto: string;
  KontoNavn: string;
  Aar: number;
  AnsvarNavn?: string | null;
  RammeomraadeID?: string | null;
  RammeomraadeNavn?: string | null;
  SektorID?: string | null;
  SektorNavn?: string | null;
}

export interface IncomeLine {
  InntektslinjeID: string;
  Aar: number;
  Enhet: string;
  Ansvar: string;
  Konto: string;
  KontoNavn: string;
  LinkCount: number;
  AnsvarNavn?: string | null;
  RammeomraadeID?: string | null;
  RammeomraadeNavn?: string | null;
  SektorID?: string | null;
  SektorNavn?: string | null;
}

export interface AgreementDocument {
  DokumentID: string;
  Filnavn_original: string;
  Analyse_status: string;
  Lastet_opp_dato: string;
  Lastet_opp_av: string;
}

export interface AuditEntry {
  AuditId: string;
  At: string;
  ActorDisplayName: string;
  Action: string;
  BeforeJson: string | null;
  AfterJson: string | null;
}

interface ListResponse {
  data: AgreementSummary[];
  pagination: { page: number; pageSize: number; totalCount: number };
}

interface DetailResponse {
  success: boolean;
  data: AgreementDetail;
}

export interface AgreementFilters {
  page: number;
  pageSize: number;
  search: string;
  kategori: string;
  erAktiv: string;
  sortBy: string;
  sortDir: string;
  ansvar: string;
  inntektslinjeID: string;
  risiko_min: string;
  stale: string;
}

export const defaultFilters: AgreementFilters = {
  page: 1,
  pageSize: 50,
  search: '',
  kategori: '',
  erAktiv: 'true',
  sortBy: 'Sist_endret',
  sortDir: 'DESC',
  ansvar: '',
  inntektslinjeID: '',
  risiko_min: '',
  stale: '',
};

function buildQueryString(filters: AgreementFilters): string {
  const params = new URLSearchParams();
  params.set('page', String(filters.page));
  params.set('pageSize', String(filters.pageSize));
  if (filters.search) params.set('search', filters.search);
  if (filters.kategori) params.set('kategori', filters.kategori);
  params.set('erAktiv', filters.erAktiv);
  params.set('sortBy', filters.sortBy);
  params.set('sortDir', filters.sortDir);
  if (filters.ansvar) params.set('ansvar', filters.ansvar);
  if (filters.inntektslinjeID) params.set('inntektslinjeID', filters.inntektslinjeID);
  if (filters.risiko_min) params.set('risiko_min', filters.risiko_min);
  if (filters.stale) params.set('stale', filters.stale);
  return params.toString();
}

export function useAgreementList(filters: AgreementFilters) {
  return useQuery({
    queryKey: ['agreements', filters],
    queryFn: () => apiGet<ListResponse>(`/agreements?${buildQueryString(filters)}`),
    placeholderData: (prev) => prev,
  });
}

export function useAgreementDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['agreement', id],
    queryFn: () => apiGet<DetailResponse>(`/agreements/${id}`),
    enabled: !!id,
  });
}

export function useAgreementPatch(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      apiPatch(`/agreements/${id}`, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement', id] });
      queryClient.invalidateQueries({ queryKey: ['agreements'] });
    },
  });
}

// --- Links ---

export function useAgreementLinks(id: string | undefined) {
  return useQuery({
    queryKey: ['agreement-links', id],
    queryFn: () => apiGet<{ success: boolean; data: AgreementLink[] }>(`/agreements/${id}/links`),
    enabled: !!id,
  });
}

export function useCreateLink(avtaleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { inntektslinjeID: string; beloepAndel?: number; andelProsent?: number }) =>
      apiPost(`/agreements/${avtaleId}/links`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement-links', avtaleId] });
      queryClient.invalidateQueries({ queryKey: ['agreement', avtaleId] });
    },
  });
}

export function usePatchLink(avtaleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, ...body }: { linkId: string; beloepAndel?: number; andelProsent?: number }) =>
      apiPatch(`/agreements/${avtaleId}/links/${linkId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement-links', avtaleId] });
      queryClient.invalidateQueries({ queryKey: ['agreement', avtaleId] });
    },
  });
}

export function useDeleteLink(avtaleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      apiDelete(`/agreements/${avtaleId}/links/${linkId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement-links', avtaleId] });
      queryClient.invalidateQueries({ queryKey: ['agreement', avtaleId] });
    },
  });
}

// --- Income Lines search ---

interface IncomeLineListResponse {
  data: IncomeLine[];
  pagination: { page: number; pageSize: number; totalCount: number };
}

export function useIncomeLineSearch(search: string, aar?: string) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (aar) params.set('aar', aar);
  params.set('pageSize', '20');

  return useQuery({
    queryKey: ['income-lines', search, aar],
    queryFn: () => apiGet<IncomeLineListResponse>(`/income-lines?${params.toString()}`),
    enabled: search.length >= 2,
  });
}

// --- Drivere (variable inntekter, epic 7) ---

export interface AgreementDriver {
  DriverID: string;
  AvtaleID: string;
  Aar: number;
  Betaler: string | null;
  Navn: string;
  Enhet: string | null;
  Volum: number | null;
  Enhetspris: number | null;
  Paaslag_prosent: number | null;
  Fastledd: number | null;
  Beregnet_beloep: number | null;
  Beregningsregel: string | null;
  Kilde: string | null;
  Notat: string | null;
  Opprettet_dato: string;
}

/** Body til POST/PATCH — camelCase, matcher admin-api-kontrakten. */
export interface DriverBody {
  aar?: number;
  navn?: string;
  betaler?: string | null;
  enhet?: string | null;
  volum?: number | null;
  enhetspris?: number | null;
  paaslagProsent?: number | null;
  fastledd?: number | null;
  beregningsregel?: string | null;
  kilde?: string | null;
  notat?: string | null;
}

export function useAgreementDrivers(id: string | undefined) {
  return useQuery({
    queryKey: ['agreement-drivers', id],
    queryFn: () => apiGet<{ success: boolean; data: AgreementDriver[] }>(`/agreements/${id}/drivers`),
    enabled: !!id,
  });
}

export function useCreateDriver(avtaleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DriverBody) => apiPost(`/agreements/${avtaleId}/drivers`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement-drivers', avtaleId] });
      queryClient.invalidateQueries({ queryKey: ['agreement', avtaleId] });
    },
  });
}

export function usePatchDriver(avtaleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ driverId, body }: { driverId: string; body: DriverBody }) =>
      apiPatch(`/agreements/${avtaleId}/drivers/${driverId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement-drivers', avtaleId] });
      queryClient.invalidateQueries({ queryKey: ['agreement', avtaleId] });
    },
  });
}

export function useDeleteDriver(avtaleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (driverId: string) => apiDelete(`/agreements/${avtaleId}/drivers/${driverId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agreement-drivers', avtaleId] });
      queryClient.invalidateQueries({ queryKey: ['agreement', avtaleId] });
    },
  });
}
